import { cn } from '../../lib/cn';
import { formatNumber, formatPercent, NO_DATA_DASH } from '../../lib/format';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from '../Table';

/**
 * MAPA DE COORTES — linha = dia de entrada, coluna = marco (D0…D90).
 *
 * Evolui o `RampCell` do Histórico: aqui a célula carrega a FAIXA que o servidor decidiu
 * (contra a mediana do workspace) em tokens de status, e o valor sempre aparece em texto — a
 * cor nunca é o único canal. Célula de marco não vencido é hachurada com "—": não é 0%, é o
 * calendário (`metricas.md` §4).
 */
export type CohortBand = 'BOA' | 'ATENCAO' | 'RUIM';

export interface CohortCell {
  /** Fração (evasão). `null` = marco não vencido. */
  value: number | null;
  band: CohortBand | null;
}

export interface CohortRowData {
  key: string;
  label: string;
  size: number;
  cells: readonly CohortCell[];
}

export interface CohortHeatmapProps {
  caption: string;
  milestones: readonly number[];
  rows: readonly CohortRowData[];
  sizeLabel?: string;
  /** Texto do "—" (tooltip nativo). */
  pendingHint?: string;
  className?: string;
}

const BAND: Record<CohortBand, { cls: string; label: string }> = {
  BOA: { cls: 'bg-success-soft text-success', label: 'dentro do normal' },
  ATENCAO: { cls: 'bg-warning-soft text-warning', label: 'acima do normal' },
  RUIM: { cls: 'bg-danger-soft text-danger font-semibold', label: 'mais que o dobro do normal' },
};

export function CohortHeatmap({
  caption,
  milestones,
  rows,
  sizeLabel = 'Turma',
  pendingHint = 'Ainda não deu tempo: este marco não venceu para esta turma.',
  className,
}: CohortHeatmapProps) {
  return (
    <Table caption={caption} density="dense" className={className}>
      <TableHeader>
        <TableRow>
          <TableHead>Entraram em</TableHead>
          <TableHead numeric>{sizeLabel}</TableHead>
          {milestones.map((m) => (
            <TableHead key={m} numeric>
              D{m}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableRowHeader>{row.label}</TableRowHeader>
            <TableCell numeric>{formatNumber(row.size)}</TableCell>
            {row.cells.map((cell, i) => {
              const marco = milestones[i];
              if (cell.value === null) {
                return (
                  <TableCell key={marco} numeric nowrap>
                    <span
                      className="nums block rounded-sm bg-hatch px-1.5 py-0.5 text-center text-muted-foreground"
                      title={pendingHint}
                      aria-label={`D${marco}: ${pendingHint}`}
                    >
                      {NO_DATA_DASH}
                    </span>
                  </TableCell>
                );
              }
              const band = cell.band ? BAND[cell.band] : null;
              const texto = formatPercent(cell.value, 0);
              return (
                <TableCell key={marco} numeric nowrap>
                  <span
                    className={cn(
                      'nums block rounded-sm px-1.5 py-0.5 text-center',
                      band ? band.cls : 'bg-muted text-foreground',
                    )}
                    title={band ? `${texto} — ${band.label}` : texto}
                    aria-label={band ? `D${marco}: ${texto}, ${band.label}` : `D${marco}: ${texto}`}
                  >
                    {texto}
                  </span>
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
